import { record, str, strList } from '@/features/brandHub/fields';

/** Every kind of template the hub stores; HTML5 archives are the only one so far. */
export const TEMPLATE_TYPES = ['html5'] as const;
export type TemplateType = (typeof TEMPLATE_TYPES)[number];

/** Every format a template may declare; the backend maps the archive's ad.size to the closest one. */
export const TEMPLATE_FORMATS = ['1:1', '4:5', '9:16', '16:9', '1.91:1', '4:1', '2:3'] as const;

export interface TemplateSize {
  width: number;
  height: number;
}

export interface Template {
  id: string;
  name: string;
  templateType: TemplateType;
  fileObject: string;
  tags: string[];
  format: string;
  size: TemplateSize | null;
}

/** What inspect reads from the archive's ad.size meta tag. Null means it could not tell. */
export interface TemplateInspection {
  format: string | null;
  width: number | null;
  height: number | null;
}

function templateType(raw: unknown): TemplateType {
  return TEMPLATE_TYPES.find((type) => type === raw) ?? 'html5';
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function parseTemplateSize(raw: unknown): TemplateSize | null {
  const size = record(raw);
  const width = nullableNumber(size.width);
  const height = nullableNumber(size.height);
  return width !== null && height !== null ? { width, height } : null;
}

export function parseTemplateInspection(raw: Record<string, unknown>): TemplateInspection {
  return {
    format: typeof raw.format === 'string' ? raw.format : null,
    width: nullableNumber(raw.width),
    height: nullableNumber(raw.height),
  };
}

export function parseTemplate(id: string, raw: Record<string, unknown>): Template | null {
  const name = str(raw.name);
  if (!name) return null;
  return {
    id,
    name,
    templateType: templateType(raw.template_type),
    fileObject: str(raw.file_object),
    tags: strList(raw.tags),
    format: str(raw.format),
    size: parseTemplateSize(raw.size),
  };
}
